import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, ACCENT_COLOR, cardSx, panelHeaderSx, tableHeadRowSx, tableRowSx, scrollbarSx, footerBarSx, fmt, fmtBtc } from '../utils';
import InfoTooltip from './InfoTooltip';

const categoryLabels = {
  // Mining cash flow (NiceHash ↔ Us)
  engine_start: 'Engine Deposits',
  engine_refill: 'Engine Refills',
  engine_stop: 'Engine Returns',
  block_reward: 'Block Rewards Mined',
  // Distributions (Us → Players)
  player_block_shares: 'Block Reward Shares',
  player_boss_winnings: 'Boss Winnings Paid',
  airdrop: 'Airdrops Distributed',
  // Wagering (Players → Us)
  normal_bids: 'Normal Bids',
  auto_bids: 'Auto Bids',
  boss_bids: 'Boss Bids (Winning)'
};

const sectionLabels = {
  mining: 'Mining Cash Flow (NiceHash)',
  distribution: 'Distributions (Us → Players)',
  wagering: 'Wagering (Players → Us)'
};

/** Build section-tagged rows from netMetrics breakdown (primary) or summary fallback */
function buildRows(netMetrics, summary) {
  const breakdown = netMetrics?.breakdown || [];
  if (breakdown.length > 0) {
    return breakdown.map((r) => ({
      category: categoryLabels[r.category] || r.category,
      direction: r.direction,
      totalBtc: r.totalBtc,
      totalUsd: r.totalUsd,
      count: r.count,
      section: r.section || 'mining'
    }));
  }
  if (!summary) return [];
  // Fallback: build from summary aggregates (same shape as backend would return)
  return [
    // Mining cash flow (NiceHash ↔ Us)
    { category: categoryLabels.engine_start, direction: 'out', totalBtc: summary.totalEngineStartsBtc || 0, totalUsd: summary.totalEngineStartsUsd || 0, count: 0, section: 'mining' },
    { category: categoryLabels.engine_refill, direction: 'out', totalBtc: summary.totalEngineRefillsBtc || 0, totalUsd: summary.totalEngineRefillsUsd || 0, count: 0, section: 'mining' },
    { category: categoryLabels.engine_stop, direction: 'in', totalBtc: summary.totalEngineStopsBtc || 0, totalUsd: summary.totalEngineStopsUsd || 0, count: 0, section: 'mining' },
    { category: categoryLabels.block_reward, direction: 'in', totalBtc: summary.totalBlockRewardBtc || 0, totalUsd: summary.totalBlockRewardUsd || 0, count: summary.totalBlocksMined || 0, section: 'mining' },
    // Distributions (Us → Players)
    { category: categoryLabels.player_block_shares, direction: 'out', totalBtc: summary.totalBlockSharesBtc || 0, totalUsd: summary.totalBlockSharesUsd || 0, count: 0, section: 'distribution' },
    { category: categoryLabels.player_boss_winnings, direction: 'out', totalBtc: summary.totalBossWinningsBtc || 0, totalUsd: summary.totalBossWinningsUsd || 0, count: 0, section: 'distribution' },
    { category: categoryLabels.airdrop, direction: 'out', totalBtc: summary.totalAirdropsBtc || 0, totalUsd: summary.totalAirdropsUsd || 0, count: summary.totalAirdropsCount || 0, section: 'distribution' },
    // Wagering (Players → Us)
    { category: categoryLabels.normal_bids, direction: 'in', totalBtc: summary.normalBidsBtc || 0, totalUsd: summary.normalBidsUsd || 0, count: summary.normalBidsCount || 0, section: 'wagering' },
    { category: categoryLabels.auto_bids, direction: 'in', totalBtc: summary.autoBidsBtc || 0, totalUsd: summary.autoBidsUsd || 0, count: summary.autoBidsCount || 0, section: 'wagering' },
    { category: categoryLabels.boss_bids, direction: 'in', totalBtc: summary.winningBossBidsBtc || 0, totalUsd: summary.winningBossBidsUsd || 0, count: summary.winningBossBidsCount || 0, section: 'wagering' }
  ];
}

/** Spacer row inserted before every section except the first — creates visible gap between sections */
function SectionSpacer({ t }) {
  return (
    <TableRow sx={{ '& td': { p: 0, border: 'none', height: '10px', background: 'transparent' } }}>
      <TableCell colSpan={5}>
        <MDBox sx={{ height: '2px', background: t.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
      </TableCell>
    </TableRow>
  );
}

function SectionHeaderRow({ label, tooltip, t, first }) {
  return (
    <>
      {!first && <SectionSpacer t={t} />}
      <TableRow sx={{
        background: t.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
        '& td': { borderBottom: `1px solid ${t.divider}` }
      }}>
        <TableCell colSpan={5} sx={{ py: 1, pl: { xs: 2, sm: 3 } }}>
          <MDBox display='flex' alignItems='center' gap={0.75}>
            <MDBox sx={{ width: '3px', height: '14px', borderRadius: '1.5px', background: ACCENT_COLOR, flexShrink: 0 }} />
            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: t.isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)' }}>
              {label}
            </MDTypography>
            {tooltip && <InfoTooltip text={tooltip} />}
          </MDBox>
        </TableCell>
      </TableRow>
    </>
  );
}

function SubNetRow({ label, btc, usd, tooltip, t }) {
  return (
    <TableRow sx={{
      background: t.isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)',
      '& td': {
        borderTop: `1px dashed ${t.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        borderBottom: `1px solid ${t.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
        py: '7px'
      }
    }}>
      <TableCell colSpan={3} sx={{ pl: { xs: 2, sm: 3 } }}>
        <MDBox display='flex' alignItems='center' gap={0.5}>
          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', color: t.isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)' }}>
            {label}
          </MDTypography>
          {tooltip && <InfoTooltip text={tooltip} />}
        </MDBox>
      </TableCell>
      <TableCell align='right'>
        <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', color: btc >= 0 ? '#1fa678' : '#db3f57' }}>
          {fmtBtc(btc)}
        </MDTypography>
      </TableCell>
      <TableCell align='right'>
        <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.76rem', color: usd >= 0 ? '#1fa678' : '#db3f57' }}>
          ${fmt(usd)}
        </MDTypography>
      </TableCell>
    </TableRow>
  );
}

function NetPLBreakdown({ netMetrics, summary, engineInfo, liveState, nicehashBalance, loading }) {
  const t = useDashboardTheme();
  const rows = buildRows(netMetrics, summary);
  const miningRows = rows.filter((r) => r.section === 'mining');
  const distributionRows = rows.filter((r) => r.section === 'distribution');
  const wageringRows = rows.filter((r) => r.section === 'wagering');

  // During live sessions, estimate engine return if no engine_stop metric exists yet
  const isLive = liveState?.eventStatus === 'live';
  const hasEngineStopRow = miningRows.some((r) => r.category === categoryLabels.engine_stop && r.totalBtc > 0);
  const engineAvail = isLive && !hasEngineStopRow && engineInfo?.availableAmount > 0
    ? Math.max(0, (Number(engineInfo.availableAmount) || 0) - (Number(engineInfo.paidAmount) || 0)) + (Number(engineInfo.airdropAmount) || 0)
    : 0;

  // Mining sub-totals
  const miningNetBtc = netMetrics?.miningNetBtc ?? summary?.miningNetBtc ?? 0;
  const miningNetUsd = netMetrics?.miningNetUsd ?? summary?.miningNetUsd ?? 0;

  // Engine net cost (from backend or compute from summary)
  const engineNetCostBtc = netMetrics?.engineNetCostBtc ?? summary?.engineNetCostBtc ?? 0;
  const engineNetCostUsd = netMetrics?.engineNetCostUsd ?? summary?.engineNetCostUsd ?? 0;
  const adjustedEngineNetCostBtc = engineAvail > 0 ? engineNetCostBtc - engineAvail : engineNetCostBtc;

  // Distribution sub-totals
  const distributionNetBtc = netMetrics?.distributionNetBtc ?? summary?.distributionNetBtc ?? 0;
  const distributionNetUsd = netMetrics?.distributionNetUsd ?? summary?.distributionNetUsd ?? 0;

  // Wagering sub-totals
  const wageringNetBtc = netMetrics?.wageringNetBtc ?? summary?.wageringNetBtc ?? 0;
  const wageringNetUsd = netMetrics?.wageringNetUsd ?? summary?.wageringNetUsd ?? 0;

  // Operator P&L = wagering net − engine net cost
  const operatorPnlBtc = netMetrics?.operatorPnlBtc ?? summary?.operatorPnlBtc ?? (wageringNetBtc - engineNetCostBtc);
  const operatorPnlUsd = netMetrics?.operatorPnlUsd ?? summary?.operatorPnlUsd ?? (wageringNetUsd - engineNetCostUsd);
  const adjustedOperatorPnlBtc = operatorPnlBtc + engineAvail;

  const nhBalanceBtc = nicehashBalance?.availableBtc ?? summary?.nicehashBalanceBtc ?? null;

  return (
    <MDBox mb={3}>
      <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
        <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
          Financial Breakdown
        </MDTypography>
        <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
      </MDBox>
      <Card sx={cardSx}>
        <MDBox sx={panelHeaderSx(t)}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
            Cash Flow &amp; Operator P&amp;L
          </MDTypography>
        </MDBox>
        {loading && rows.length === 0 ? (
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} />
          </MDBox>
        ) : (
          <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 'none' }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow sx={tableHeadRowSx(t)}>
                  <TableCell>Category</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell align='right'>Count</TableCell>
                  <TableCell align='right'>BTC</TableCell>
                  <TableCell align='right'>USD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* ── §1 Mining Cash Flow (NiceHash ↔ Us) ── */}
                {miningRows.length > 0 && (
                  <>
                    <SectionHeaderRow first label={sectionLabels.mining} tooltip='Cash moving between us and NiceHash: engine deposits/refills sent, engine returns received, and block rewards mined.' t={t} />
                    {miningRows.map((r, i) => {
                      return (
                        <TableRow key={`m-${i}`} sx={tableRowSx(t, { index: 0 })}>
                          <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{r.category}</MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: r.direction === 'in' ? '#1fa678' : '#db3f57' }}>
                              {r.direction === 'in' ? '↑ IN' : '↓ OUT'}
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{r.count || '—'}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(r.totalBtc)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(r.totalUsd)}</MDTypography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Estimated engine return row (live only, when no engine_stop metric) */}
                    {engineAvail > 0 && (
                      <TableRow sx={tableRowSx(t, { index: 0 })}>
                        <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                          <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText, fontStyle: 'italic' }}>Engine Return (est.)</MDTypography>
                        </TableCell>
                        <TableCell>
                          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: '#1fa678', fontStyle: 'italic' }}>
                            {'↑ IN'}
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>—</MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText, fontStyle: 'italic' }}>{fmtBtc(engineAvail)}</MDTypography>
                        </TableCell>
                        <TableCell align='right'>
                          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText, fontStyle: 'italic' }}>—</MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
                    <SubNetRow label='Engine Net Cost' btc={-adjustedEngineNetCostBtc} usd={-engineNetCostUsd} tooltip='What we actually spent on engines: deposits + refills minus returned balance.' t={t} />
                    <SubNetRow label={engineAvail > 0 ? 'Mining Gross (adj.)' : 'Mining Gross'} btc={miningNetBtc + engineAvail} usd={miningNetUsd} tooltip='Block rewards received minus engine net cost. Shows total value generated by mining.' t={t} />
                  </>
                )}

                {/* ── §2 Distributions (Us → Players) ── */}
                {distributionRows.length > 0 && (
                  <>
                    <SectionHeaderRow label={sectionLabels.distribution} tooltip='Rewards paid out from us to players: block reward shares to train position holders, boss winnings from the boss reward share of each mined block, and airdrops when the meter fills.' t={t} />
                    {distributionRows.map((r, i) => {
                      return (
                        <TableRow key={`d-${i}`} sx={tableRowSx(t, { index: 0 })}>
                          <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{r.category}</MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: r.direction === 'in' ? '#1fa678' : '#db3f57' }}>
                              {r.direction === 'in' ? '↑ IN' : '↓ OUT'}
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{r.count || '—'}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(r.totalBtc)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(r.totalUsd)}</MDTypography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <SubNetRow label='Total Distributed' btc={distributionNetBtc} usd={distributionNetUsd} tooltip='Sum of all player payouts: block reward shares + boss winnings + airdrops. Always negative (money out).' t={t} />
                  </>
                )}

                {/* ── §3 Wagering (Players → Us) ── */}
                {wageringRows.length > 0 && (
                  <>
                    <SectionHeaderRow label={sectionLabels.wagering} tooltip='Pure bid revenue from players: normal bids, auto bids, and winning boss bids.' t={t} />
                    {wageringRows.map((r, i) => {
                      return (
                        <TableRow key={`w-${i}`} sx={tableRowSx(t, { index: 0 })}>
                          <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{r.category}</MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: r.direction === 'in' ? '#1fa678' : '#db3f57' }}>
                              {r.direction === 'in' ? '↑ IN' : '↓ OUT'}
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{r.count || '—'}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(r.totalBtc)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(r.totalUsd)}</MDTypography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <SubNetRow label='Wagering Net' btc={wageringNetBtc} usd={wageringNetUsd} tooltip='Pure bid revenue from players. This is our revenue before engine costs.' t={t} />
                  </>
                )}

                {/* ── §4 Operator P&L ── */}
                <SectionHeaderRow label='Operator P&L' tooltip='Our bottom line: Wagering Net minus Engine Net Cost. Block rewards are pass-through (100% distributed) and do not affect this figure.' t={t} />
                <TableRow sx={tableRowSx(t, { index: 0 })}>
                  <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                    <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>Wagering Net</MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: wageringNetBtc >= 0 ? '#1fa678' : '#db3f57' }}>
                      {wageringNetBtc >= 0 ? '↑ Revenue' : '↓ Loss'}
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right' />
                  <TableCell align='right'>
                    <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(wageringNetBtc)}</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(wageringNetUsd)}</MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow sx={tableRowSx(t, { index: 0 })}>
                  <TableCell sx={{ pl: { xs: 3.5, sm: 5 } }}>
                    <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>
                      {engineAvail > 0 ? 'Engine Net Cost (adj.)' : 'Engine Net Cost'}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: '#db3f57' }}>
                      ↓ Cost
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right' />
                  <TableCell align='right'>
                    <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(adjustedEngineNetCostBtc)}</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(engineNetCostUsd)}</MDTypography>
                  </TableCell>
                </TableRow>

                {/* ── Operator P&L bottom line ── */}
                <TableRow sx={{
                  background: t.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)',
                  '& td': {
                    borderTop: `2px solid ${t.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                    borderBottom: 'none',
                    py: '10px'
                  }
                }}>
                  <TableCell colSpan={3} sx={{ pl: { xs: 2, sm: 3 } }}>
                    <MDBox display='flex' alignItems='center' gap={0.75}>
                      <MDBox sx={{ width: '3px', height: '16px', borderRadius: '1.5px', background: adjustedOperatorPnlBtc >= 0 ? '#1fa678' : '#db3f57', flexShrink: 0 }} />
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.88rem', color: t.primaryText }}>
                        {engineAvail > 0 ? 'OPERATOR P&L (adj.)' : 'OPERATOR P&L'}
                      </MDTypography>
                    </MDBox>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.86rem', color: adjustedOperatorPnlBtc >= 0 ? '#1fa678' : '#db3f57' }}>
                      {fmtBtc(adjustedOperatorPnlBtc)}
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.86rem', color: operatorPnlUsd >= 0 ? '#1fa678' : '#db3f57' }}>
                      ${fmt(operatorPnlUsd)}
                    </MDTypography>
                  </TableCell>
                </TableRow>

                {/* NH Account Balance reference row */}
                {nhBalanceBtc != null && (
                  <TableRow sx={{
                    background: t.isDark ? 'rgba(245,158,11,0.05)' : 'rgba(245,158,11,0.03)',
                    '& td': {
                      borderTop: `1px solid ${t.isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.2)'}`,
                      borderBottom: 'none',
                      py: '8px'
                    }
                  }}>
                    <TableCell colSpan={3} sx={{ pl: { xs: 2, sm: 3 } }}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.72rem', color: '#f59e0b', letterSpacing: '0.02em' }}>
                        NH Account Balance
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: '#f59e0b' }}>
                        {fmtBtc(nhBalanceBtc)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='caption' sx={{ fontSize: '0.66rem', color: t.secondaryText, fontStyle: 'italic' }}>
                        All engines
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <MDBox sx={footerBarSx(t)}>
          <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: t.footerText }}>
            Block rewards are pass-through (100% distributed to players, including boss winnings). Operator P&amp;L = Wagering Net − Engine Net Cost.
          </MDTypography>
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default NetPLBreakdown;
